import { useEffect, useState, type ReactNode } from "react";
import useSWR from "swr";
import { useLocalStorage } from "@mantine/hooks";
import { Accordion, ActionIcon, Alert, Badge, Button, Checkbox, Group, List, Modal, NumberInput, Paper, PasswordInput, Popover, Spoiler, Stack, Table, Text, TextInput, Textarea } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import Pencil from "lucide-react/dist/esm/icons/pencil";
import Send from "lucide-react/dist/esm/icons/send";
import { useFetch, previewAwhExport, createAwhExport, cancelAwhExportTask, getAwhExports, AwhExport, AwhExportFilter, AwhExportOperation, AwhExportPreview, AwhExportTask, MiptTechCredentials } from "../../../shared/api";
import { ErrorDisplay, Loading, extractLogin, formatDateTime } from "../../../shared/utils";
import { InfoHint, WithHint } from "../../../shared/components/InfoHint";

const ROLE_LABEL: Record<string, string> = { operator: "оператор", editor: "монтажёр" };

const currentMonth = () => { const d = new Date(); return `${d.toLocaleString("ru", { month: "long" })} ${d.getFullYear()}`; };

/** Local midnight of a "YYYY-MM-DD" date, `days` later, as an ISO timestamp. */
const localMidnight = (date: string, days = 0) => { const d = new Date(`${date}T00:00`); d.setDate(d.getDate() + days); return d.toISOString(); };

const TASK_NAME_MAX = 150;

/** One mipt.tech task: its name heads the people column, level with the ЧРА and ДК headers. */
function TaskCard({ title, operations, description }: { title: ReactNode; operations: AwhExportOperation[]; description?: string }) {
  return (
    <Paper withBorder p="md">
      <Table withRowBorders={false} verticalSpacing={4} fz="sm" layout="fixed">
        <Table.Thead>
          <Table.Tr style={{ verticalAlign: "top" }}>
            <Table.Th pl={0} pb="xs">{title}</Table.Th>
            <Table.Th w={70} pb="xs"><Text fz="xs" c="dimmed" fw={600}>ЧРА</Text></Table.Th>
            <Table.Th w={50} pb="xs"><Text fz="xs" c="dimmed" fw={600}>ДК</Text></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {operations.map((op, i) => (
            <Table.Tr key={i}>
              <Table.Td pl={0}>{op.name}{op.student_id && <Text span c="dimmed" fz="xs"> {extractLogin(op.student_id)}</Text>}</Table.Td>
              <Table.Td>{op.hours}</Table.Td>
              <Table.Td>{op.money_coef}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {description && (
        <Spoiler maxHeight={0} showLabel="Описание задачи" hideLabel="Скрыть описание" fz="xs" mt="xs">
          <Text fz="xs" c="dimmed" style={{ whiteSpace: "pre-wrap" }}>{description}</Text>
        </Spoiler>
      )}
    </Paper>
  );
}

function TaskResult({ task, onCancel }: { task: AwhExportTask; onCancel?: () => void }) {
  const ok = task.state === "success", cancelled = !!task.cancelled_at;
  return (
    <TaskCard operations={task.operations} title={
      <Stack gap={4}>
        <Group gap="xs" wrap="nowrap" align="flex-start">
          <Text fw={600} fz="md" style={{ flex: 1 }} td={cancelled ? "line-through" : undefined}>{task.name}</Text>
          {cancelled
            ? <Badge variant="light" color="gray" style={{ flexShrink: 0 }}>Отменено</Badge>
            : <Badge variant="light" color={ok ? "green" : "red"} style={{ flexShrink: 0 }}>{ok ? "Начислено" : "Ошибка"}</Badge>}
          {!cancelled && onCancel && <Button size="compact-xs" variant="subtle" color="red" onClick={onCancel} style={{ flexShrink: 0 }}>Отменить</Button>}
        </Group>
        {task.mipt_task_id != null && <Text fz="xs" c="dimmed" fw={400}>Задача mipt.tech #{task.mipt_task_id}</Text>}
        {cancelled && <Text fz="xs" c="dimmed" fw={400}>Отменил {task.cancelled_by} {formatDateTime(task.cancelled_at!)}: записи вернулись в «Ожидание»</Text>}
        {task.error && <Text fz="xs" fw={400} c={ok ? "yellow.7" : "red.6"}>{task.error}</Text>}
      </Stack>
    } />
  );
}

/** Takes an exported task back: deletes it on mipt.tech and returns its records to pending. */
function CancelTaskModal({ task, onClose, onDone }: { task: AwhExportTask | null; onClose: () => void; onDone: () => void }) {
  const { fetch } = useFetch();
  const [miptUsername, setMiptUsername] = useLocalStorage<string>({ key: "admin:awh-export:mipt-username", defaultValue: "", getInitialValueInEffect: false });
  const [miptPassword, setMiptPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setError(null); if (!task) setMiptPassword(""); }, [task]);

  const confirm = async () => {
    if (!task) return;
    setBusy(true); setError(null);
    try { await cancelAwhExportTask({ taskId: task.id, credentials: { username: miptUsername.trim(), password: miptPassword }, fetch }); onDone(); onClose(); }
    catch (e: any) { setError(e?.message ?? String(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal opened={task !== null} onClose={onClose} title="Отменить выставление" centered closeOnClickOutside={!busy}>
      <Stack>
        <Text fz="sm">
          {task?.mipt_task_id != null ? <>Задача mipt.tech #{task.mipt_task_id} «{task.name}» будет удалена там, </> : <>Задача «{task?.name}» </>}
          а её записи ЧРА вернутся в «Ожидание», и их можно будет выставить заново.
        </Text>
        {task?.mipt_task_id != null && (
          <Group grow align="flex-start">
            <TextInput label="Логин mipt.tech" autoComplete="username" value={miptUsername} onChange={e => setMiptUsername(e.target.value)} />
            <PasswordInput label="Пароль mipt.tech" autoComplete="current-password" value={miptPassword} onChange={e => setMiptPassword(e.target.value)} />
          </Group>
        )}
        {error && <Alert color="red">{error}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={busy}>Не отменять</Button>
          {/* A task that never reached mipt.tech needs no login: the server only resets the records. */}
          <Button color="red" loading={busy} disabled={task?.mipt_task_id != null && (!miptUsername.trim() || !miptPassword)} onClick={confirm}>Отменить выставление</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** The task name, editable in a popover; too long for mipt.tech shows red. */
function TaskName({ name, onChange }: { name: string; onChange: (name: string) => void }) {
  const [draft, setDraft] = useState(name);
  const tooLong = name.length > TASK_NAME_MAX;
  return (
    <Stack gap={2}>
      <Group gap={6} wrap="nowrap" align="flex-start">
        <Text fw={600} fz="md" c={tooLong ? "red.6" : undefined} style={{ flex: 1 }}>{name}</Text>
        <Popover width={420} position="bottom-end" withArrow trapFocus onOpen={() => setDraft(name)}>
          <Popover.Target><ActionIcon variant="subtle" color="gray" size="sm" aria-label="Изменить название задачи"><Pencil size={14} /></ActionIcon></Popover.Target>
          <Popover.Dropdown>
            <Stack gap="xs">
              <Textarea label="Название задачи на mipt.tech" autosize minRows={2} value={draft} onChange={e => setDraft(e.target.value)}
                error={draft.trim().length > TASK_NAME_MAX && `Длиннее ${TASK_NAME_MAX} символов`} description={`${draft.trim().length} / ${TASK_NAME_MAX}`} />
              <Group justify="flex-end"><Button size="xs" variant="light" disabled={!draft.trim()} onClick={() => onChange(draft.trim())}>Применить</Button></Group>
            </Stack>
          </Popover.Dropdown>
        </Popover>
      </Group>
      {tooLong && <Text fz="xs" fw={400} c="red.6">Длиннее {TASK_NAME_MAX} символов: mipt.tech его не примет, сократите</Text>}
    </Stack>
  );
}

export function AwhExportModal({ opened, onClose, onDone }: { opened: boolean; onClose: () => void; onDone: () => void }) {
  const { fetch } = useFetch();
  const [eventId, setEventId] = useLocalStorage<number | string>({ key: "admin:awh-export:event-id", defaultValue: "", getInitialValueInEffect: false });
  const [period, setPeriod] = useState(currentMonth);
  // Edited task names by subject id; kept across preview refreshes.
  const [taskNames, setTaskNames] = useState<Record<number, string>>({});
  // Subjects unticked in the preview: their tasks are left out of the export.
  const [skippedSubjects, setSkippedSubjects] = useState<Set<number>>(new Set());
  // Inclusive "YYYY-MM-DD" dates in the browser's timezone; empty leaves that end open.
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [miptUsername, setMiptUsername] = useLocalStorage<string>({ key: "admin:awh-export:mipt-username", defaultValue: "", getInitialValueInEffect: false });
  // Only ever in memory, and dropped when the dialog closes.
  const [miptPassword, setMiptPassword] = useState("");
  const [preview, setPreview] = useState<AwhExportPreview | null>(null);
  const [result, setResult] = useState<AwhExport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (opened) { setPreview(null); setResult(null); setError(null); setTaskNames({}); setSkippedSubjects(new Set()); } else setMiptPassword(""); }, [opened]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await fn(); } catch (e: any) { setError(e?.message ?? String(e)); } finally { setBusy(false); }
  };
  const rangeInvalid = !!createdFrom && !!createdTo && createdFrom > createdTo;
  const filter = (): AwhExportFilter => ({ periodLabel: period.trim(), createdFrom: createdFrom ? localMidnight(createdFrom) : undefined, createdBefore: createdTo ? localMidnight(createdTo, 1) : undefined });
  const credentials: MiptTechCredentials = { username: miptUsername.trim(), password: miptPassword };
  const hasCredentials = !!credentials.username && !!credentials.password;
  const loadPreview = () => run(async () => setPreview(await previewAwhExport({ filter: filter(), credentials, fetch })));
  const nameOf = (t: { subject_id: number; name: string }) => taskNames[t.subject_id] ?? t.name;
  const namesTooLong = !!preview?.tasks.some(t => !skippedSubjects.has(t.subject_id) && nameOf(t).length > TASK_NAME_MAX);
  const editedNames = () => Object.fromEntries((preview?.tasks ?? []).filter(t => nameOf(t) !== t.name).map(t => [t.subject_id, nameOf(t)]));
  const picked = (preview?.tasks ?? []).filter(t => !skippedSubjects.has(t.subject_id));
  // All ticked: no list, so records that turned pending since the preview go too, as before.
  const submissionIds = () => picked.length === preview?.tasks.length ? undefined : picked.flatMap(t => t.submission_ids);
  const submit = () => run(async () => { setResult(await createAwhExport({ eventId: Number(eventId), filter: filter(), credentials, taskNames: editedNames(), submissionIds: submissionIds(), fetch })); onDone(); });
  const toggleSubject = (id: number, on: boolean) => setSkippedSubjects(prev => { const next = new Set(prev); if (on) next.delete(id); else next.add(id); return next; });

  return (
    <Modal opened={opened} onClose={onClose} title="Выставить ЧРА на mipt.tech" size="xl" closeOnClickOutside={!busy} closeOnEscape={!busy} withCloseButton={!busy}>
      {result ? (
        <Stack>
          <Text fz="sm">Готово: начислено {result.tasks.filter(t => t.state === "success").length} из {result.tasks.length} задач.</Text>
          {result.tasks.map(t => <TaskResult key={t.id} task={t} />)}
          <Group justify="flex-end"><Button onClick={onClose}>Закрыть</Button></Group>
        </Stack>
      ) : (
        <Stack>
          <Group grow align="flex-start">
            <NumberInput label={<WithHint hint="Из адреса мероприятия на mipt.tech">ID мероприятия</WithHint>} min={1} allowDecimal={false} allowNegative={false} value={eventId} onChange={setEventId} />
            <TextInput label={<WithHint hint="Подставляется в название задачи вместо семестра">Период</WithHint>} value={period} onChange={e => { setPeriod(e.target.value); setPreview(null); }} />
          </Group>
          <Group grow align="flex-start">
            <DatePickerInput label={<WithHint hint="По дате создания записи ЧРА, обе даты включительно. Любую можно не указывать">Созданы с</WithHint>} placeholder="Без ограничения" valueFormat="DD.MM.YYYY" clearable
              value={createdFrom || null} maxDate={createdTo || undefined} onChange={v => { setCreatedFrom(v ?? ""); setPreview(null); }} />
            <DatePickerInput label="по" placeholder="Без ограничения" valueFormat="DD.MM.YYYY" clearable error={rangeInvalid && "Конец раньше начала"}
              value={createdTo || null} minDate={createdFrom || undefined} onChange={v => { setCreatedTo(v ?? ""); setPreview(null); }} />
          </Group>
          <Group grow align="flex-start">
            <TextInput label="Логин mipt.tech" autoComplete="username" value={miptUsername} onChange={e => setMiptUsername(e.target.value)} />
            <PasswordInput label={<WithHint hint="Только для этого выставления, нигде не сохраняется">Пароль mipt.tech</WithHint>} autoComplete="current-password" value={miptPassword} onChange={e => setMiptPassword(e.target.value)} />
          </Group>
          <Group><Button variant="light" onClick={loadPreview} loading={busy && !preview} disabled={!period.trim() || rangeInvalid || !hasCredentials || busy}>{preview ? "Обновить превью" : "Собрать превью"}</Button></Group>
          {error && <Alert color="red">{error}</Alert>}
          {preview && (
            <>
              <Group justify="space-between">
                <Text fz="sm" fw={500}>{preview.tasks.length ? `Будет создано задач: ${picked.length} из ${preview.tasks.length}` : "Выставлять нечего"}</Text>
                {preview.tasks.length > 1 && (
                  <Group gap="xs">
                    <Button size="compact-xs" variant="subtle" onClick={() => setSkippedSubjects(new Set())}>Отметить все</Button>
                    <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setSkippedSubjects(new Set(preview.tasks.map(t => t.subject_id)))}>Снять все</Button>
                  </Group>
                )}
              </Group>
              {preview.flagged.length > 0 && (
                <Alert color="orange" title={`Проверьте вручную: ${preview.flagged.length}`}>
                  <Text fz="xs" c="dimmed" mb={6}>Эти записи уйдут вместе с остальными. Если что-то не так, исправьте или удалите их во вкладке ЧРА и обновите превью.</Text>
                  <List size="sm" spacing={4}>
                    {preview.flagged.map(f => (
                      <List.Item key={f.submission_id}>
                        <Text span fz="sm" fw={500}>{f.subject_title ?? "Без предмета"}{f.lecture_number != null && `, лекция ${f.lecture_number}`} · {ROLE_LABEL[f.role] ?? f.role} {f.who} · {f.awh} ЧРА, ДК {f.money_coef}</Text>
                        {f.created_by && <Text span fz="xs" c="dimmed"> · создал {f.created_by}</Text>}
                        {/* Reasons sit under the entry, smaller than it, so the entry stays the thing to scan for. */}
                        <List size="xs" withPadding listStyleType="disc">{f.reasons.map((r, i) => <List.Item key={i}><Text span fz="xs" c="dimmed">{r}</Text></List.Item>)}</List>
                      </List.Item>
                    ))}
                  </List>
                </Alert>
              )}
              {preview.tasks.map(t => {
                const on = !skippedSubjects.has(t.subject_id);
                return (
                  <Group key={t.subject_id} align="flex-start" gap="sm" wrap="nowrap" style={{ opacity: on ? 1 : 0.5 }}>
                    <Checkbox mt="md" checked={on} onChange={e => toggleSubject(t.subject_id, e.currentTarget.checked)} aria-label={`Выставить «${nameOf(t)}»`} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <TaskCard operations={t.operations} description={t.description}
                        title={<TaskName name={nameOf(t)} onChange={name => setTaskNames(n => ({ ...n, [t.subject_id]: name }))} />} />
                    </div>
                  </Group>
                );
              })}
              {preview.skipped.length > 0 && (
                <Alert color="yellow" title={`Не уйдут: ${preview.skipped.length}`}>
                  <List size="sm" spacing={2}>
                    {preview.skipped.map(s => (
                      <List.Item key={s.submission_id}>
                        {s.subject_title ?? "Без предмета"}{s.lecture_number != null && `, лекция ${s.lecture_number}`} · {ROLE_LABEL[s.role] ?? s.role} {s.who} · {s.awh} ЧРА — <Text span fz="sm" fw={500}>{s.reason}</Text>
                      </List.Item>
                    ))}
                  </List>
                </Alert>
              )}
            </>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose} disabled={busy}>Отмена</Button>
            <Button leftSection={<Send size={16} />} onClick={submit} loading={busy && !!preview} disabled={!picked.length || !eventId || namesTooLong || busy}>
              {preview && picked.length < preview.tasks.length ? `Выставить ${picked.length} из ${preview.tasks.length}` : "Выставить"}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

export function AwhExportsTab() {
  const { fetch } = useFetch();
  const { data, error, isLoading, mutate } = useSWR("admin/awh-exports", () => getAwhExports({ fetch }));
  const [exporting, setExporting] = useState(false);
  const [cancelling, setCancelling] = useState<AwhExportTask | null>(null);

  if (error) return <ErrorDisplay error={error} />;

  return (
    <Stack>
      <Group justify="space-between">
        <Text fw={500}>Выгрузки на mipt.tech</Text>
        <Group gap={6} wrap="nowrap">
          <Button variant="light" onClick={() => setExporting(true)}>Выставить ЧРА</Button>
          <InfoHint size={18} label="Выставляются записи в статусе «Ожидание» с ЧРА больше 0: по задаче на каждый предмет. В превью видно, что уйдёт и что пропустится, ненужные задачи можно снять галочкой. Выставленную задачу можно отменить: она удалится на mipt.tech, а записи вернутся в «Ожидание». Записи «Не начислено» не выставляются — чтобы повторить, переведите их в «Ожидание»." />
        </Group>
      </Group>
      {isLoading || !data ? <Loading /> : data.length === 0 ? <Text c="dimmed" ta="center" py="md">Выгрузок ещё не было</Text> : (
        <Accordion variant="separated" multiple>
          {data.map(e => {
            const ok = e.tasks.filter(t => t.state === "success").length;
            return (
              <Accordion.Item key={e.id} value={String(e.id)}>
                <Accordion.Control>
                  <Group gap="xs" wrap="wrap">
                    <Text fz="sm" fw={500}>{formatDateTime(e.created_at)}</Text>
                    <Text fz="sm" c="dimmed">· {e.period_label} · мероприятие #{e.event_id} · {e.created_by}{e.mipt_username && ` (mipt.tech: ${e.mipt_username})`}</Text>
                    <Badge variant="dot" color={ok === e.tasks.length ? "green" : "red"}>{e.finished_at ? `${ok} / ${e.tasks.length}` : "идёт"}</Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel><Stack gap="xs">{e.tasks.map(t => <TaskResult key={t.id} task={t} onCancel={e.finished_at ? () => setCancelling(t) : undefined} />)}</Stack></Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
      )}
      <AwhExportModal opened={exporting} onClose={() => setExporting(false)} onDone={() => mutate()} />
      <CancelTaskModal task={cancelling} onClose={() => setCancelling(null)} onDone={() => mutate()} />
    </Stack>
  );
}
